import { store, toggleVerseMemorized } from '../store.js';
import { showToast } from '../utils/toast.js';
import confetti from 'canvas-confetti';

let activeVerseFilter = 'all';

export function renderVerseTreasury(container) {
  const lessonsWithVerses = store.lessons.filter(l => l.verse_ref && l.verse_text);
  const memorizedCount = lessonsWithVerses.filter(l => store.progress[l.id]?.verse_memorized).length;
  const percentage = lessonsWithVerses.length > 0 ? Math.round((memorizedCount / lessonsWithVerses.length) * 100) : 0;

  const filteredVerses = activeVerseFilter === 'all' 
    ? lessonsWithVerses 
    : lessonsWithVerses.filter(l => (l.module_name || '').toLowerCase().includes(activeVerseFilter.toLowerCase()));

  container.innerHTML = `
    <div class="content-container">
      <div style="text-align:center; max-width:800px; margin:0 auto 2.5rem;">
        <span class="hero-badge"><i class="fa-solid fa-layer-group"></i> Scripture Mastery</span>
        <h1 class="hero-title" style="font-size:2.6rem;">Memory Verses Treasury</h1>
        <p class="hero-desc" style="font-size:1.05rem;">
          “I have stored up your word in my heart, that I might not sin against you.” — Psalm 119:11<br>
          Review, recite, and master all 21 core scriptures embedded throughout the discipleship journey.
        </p>

        <!-- Progress bar -->
        <div style="background:var(--bg-card); border:1px solid var(--border-subtle); border-radius:var(--radius-xl); padding:1.25rem 2rem; margin-top:1.5rem; display:flex; align-items:center; justify-content:space-between; gap:1.5rem; flex-wrap:wrap;">
          <div>
            <div style="font-size:0.85rem; color:var(--text-muted); font-weight:600; text-transform:uppercase;">Mastery Progress</div>
            <div style="font-size:1.4rem; font-weight:700; color:var(--accent-gold);">${memorizedCount} of ${lessonsWithVerses.length} Memorized</div>
          </div>
          <div style="flex:1; min-width:200px;">
            <div class="progress-track">
              <div class="progress-fill" style="width:${percentage}%; background:var(--grad-gold);"></div>
            </div>
          </div>
          <div style="font-weight:700; font-size:1.1rem; color:var(--accent-gold);">${percentage}%</div>
        </div>
      </div>

      <!-- Filters -->
      <div style="display:flex; justify-content:center; gap:0.5rem; margin-bottom:2rem; flex-wrap:wrap;">
        <button class="nav-link-btn ${activeVerseFilter === 'all' ? 'active' : ''}" data-filter="all">
          All Verses (21)
        </button>
        <button class="nav-link-btn ${activeVerseFilter === 'forming' ? 'active' : ''}" data-filter="forming">
          Forming (Seekers)
        </button>
        <button class="nav-link-btn ${activeVerseFilter === 'storming' ? 'active' : ''}" data-filter="storming">
          Storming (Believers)
        </button>
        <button class="nav-link-btn ${activeVerseFilter === 'performing' ? 'active' : ''}" data-filter="performing">
          Performing (Reproducers)
        </button>
      </div>

      <!-- Grid of Flashcards -->
      <div style="display:grid; grid-template-columns:repeat(auto-fill, minmax(320px, 1fr)); gap:1.5rem;">
        ${filteredVerses.map(l => {
          const isDone = store.progress[l.id]?.verse_memorized;

          return `
            <div class="module-card" style="padding:1.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:280px;" data-card-id="${l.id}">
              <div>
                <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:1rem;">
                  <span style="font-size:0.75rem; font-weight:700; color:var(--text-muted); text-transform:uppercase;">
                    Lesson ${l.overall_order} • ${(l.module_name || '').split(' ')[0]}
                  </span>
                  ${isDone ? `
                    <span style="color:var(--accent-gold); font-size:0.8rem; font-weight:700; display:flex; align-items:center; gap:4px;">
                      <i class="fa-solid fa-star"></i> Memorized
                    </span>
                  ` : ''}
                </div>

                <div class="verse-ref-large" style="font-size:1.6rem; color:var(--accent-blue); margin-bottom:0.75rem;">
                  ${l.verse_ref}
                </div>

                <div class="verse-text-preview" style="font-family:var(--font-scripture); font-size:1.15rem; font-style:italic; line-height:1.6; color:var(--text-secondary); margin-bottom:1rem;">
                  “${l.verse_text}”
                </div>
              </div>

              <div style="display:flex; justify-content:space-between; align-items:center; border-top:1px solid var(--border-subtle); padding-top:1rem; margin-top:1rem;">
                <div style="display:flex; gap:0.5rem;">
                  <button class="icon-btn" data-audio-ref="${l.verse_ref}" data-audio-text="${l.verse_text.replace(/"/g, '&quot;')}" title="Listen Aloud">
                    <i class="fa-solid fa-volume-high"></i>
                  </button>
                  <button class="icon-btn" data-copy-verse="${l.verse_ref} - &quot;${l.verse_text.replace(/"/g, '&quot;')}&quot;" title="Copy to clipboard">
                    <i class="fa-regular fa-copy"></i>
                  </button>
                </div>

                <div style="display:flex; gap:0.5rem; align-items:center;">
                  <button class="${isDone ? 'btn-gold' : 'btn-secondary'}" data-toggle-mem="${l.id}" style="padding:0.4rem 0.8rem; font-size:0.82rem;">
                    <i class="fa-solid ${isDone ? 'fa-check' : 'fa-star'}"></i> ${isDone ? 'Memorized' : 'Mark Memorized'}
                  </button>
                  <a href="#lesson/${l.id}" class="icon-btn" title="Open Lesson">
                    <i class="fa-solid fa-arrow-up-right-from-square"></i>
                  </a>
                </div>
              </div>
            </div>
          `;
        }).join('')}
      </div>
    </div>
  `;

  // Attach filter handlers
  const filterBtns = container.querySelectorAll('[data-filter]');
  filterBtns.forEach(btn => {
    btn.onclick = () => {
      activeVerseFilter = btn.getAttribute('data-filter');
      renderVerseTreasury(container);
    };
  });

  // Attach audio buttons
  container.querySelectorAll('[data-audio-text]').forEach(btn => {
    btn.onclick = () => {
      const ref = btn.getAttribute('data-audio-ref');
      const text = btn.getAttribute('data-audio-text');
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const u = new SpeechSynthesisUtterance(`${ref}. ${text}`);
        u.rate = 0.95;
        window.speechSynthesis.speak(u);
        showToast('Playing scripture recitation audio...', 'info');
      }
    };
  });

  // Attach copy buttons
  container.querySelectorAll('[data-copy-verse]').forEach(btn => {
    btn.onclick = async () => {
      const verse = btn.getAttribute('data-copy-verse');
      await navigator.clipboard.writeText(verse);
      showToast('Verse copied to clipboard!', 'success');
    };
  });

  // Attach toggle memorized
  container.querySelectorAll('[data-toggle-mem]').forEach(btn => {
    btn.onclick = async () => {
      const lid = btn.getAttribute('data-toggle-mem');
      const nowMem = await toggleVerseMemorized(lid);
      if (nowMem) {
        confetti({ particleCount: 50, spread: 50 });
        showToast('Marked as memorized!', 'success');
      }
      renderVerseTreasury(container);
    };
  });
}
